import { NgFor, NgIf } from '@angular/common';
import { Component, computed, effect, Input, OnInit, Signal, signal, WritableSignal } from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatCard } from '@angular/material/card';
import { MatIcon } from '@angular/material/icon';
import { Sort } from '@angular/material/sort';
import { TranslateModule } from '@ngx-translate/core';
import { IKanbanColumnDef, KanbanViewComponent } from '../../../../shared/components/kanban-view/kanban-view.component';
import { IDropdownOption, MatDropdown } from '../../../../shared/components/mat-dropdown/mat-dropdown.component';
import { IKanbanColumn, IKanbanViewOption } from '../../../../shared/services/kanban-column/kanban-column.interface';
import { KanbanColumnService } from '../../../../shared/services/kanban-column/kanban-column.service';
import { ELoadStatus } from '../../../../state/state.interface';
import { HttpUtilitiesService } from '../../../../utilities/http-utilities.service';
import { ItemsViewControlsComponent } from '../../items-view-controls/items-view-controls.component';
import { BaseItemView } from '../base-item-view.component';
import { ItemViewStore } from '../item-view.store';
import { IDatatableItem } from './items-kanban.interface';

const KANBAN_VIEW_STORAGE_KEY = 'selectedKanbanViewId';

@Component({
  imports: [ItemsViewControlsComponent, KanbanViewComponent, MatCard, MatDropdown, MatIcon, NgFor, NgIf, ReactiveFormsModule, TranslateModule],
  providers: [HttpUtilitiesService, ItemViewStore],
  selector: 'app-items-kanban',
  styleUrls: ['items-kanban.component.scss'],
  templateUrl: './items-kanban.component.html',
})
export class ItemsKanbanComponent extends BaseItemView implements OnInit {
  @Input() public boardKey: string | null = null;

  public readonly items: Signal<IDatatableItem[]> = computed(() => {
    return this.store.data$().map(this.formatData);
  });

  public readonly kanbanViews: WritableSignal<IKanbanViewOption[]> = signal<IKanbanViewOption[]>([]);
  public readonly viewsLoaded: WritableSignal<boolean> = signal<boolean>(false);
  public readonly columnsLoaded: WritableSignal<boolean> = signal<boolean>(false);
  public readonly columnDefs: WritableSignal<IKanbanColumnDef[]> = signal<IKanbanColumnDef[]>([]);
  public selectedViewId: number | null = null;

  // The view selector is a single-select mat-dropdown; its FormControl holds a one-item option array.
  public readonly viewControl = new FormControl([] as IDropdownOption[]);

  public readonly viewOptions: Signal<IDropdownOption[]> = computed(() =>
    this.kanbanViews().map((view: IKanbanViewOption): IDropdownOption => ({ id: view.id, name: this.viewLabel(view) })),
  );

  public readonly hasViews: Signal<boolean> = computed(() => this.kanbanViews().length > 0);

  private readonly itemsLoaded: Signal<boolean> = computed(
    () => this.store.dataStatus$() === ELoadStatus.success || this.store.dataStatus$() === ELoadStatus.failure,
  );

  public readonly initialLoadDone: WritableSignal<boolean> = signal<boolean>(false);

  constructor(private readonly kanbanColumnService: KanbanColumnService) {
    super('kanban');

    effect(() => {
      const readyForFirstPaint: boolean =
        this.viewsLoaded() && (!this.hasViews() || (this.columnsLoaded() && this.itemsLoaded()));

      if (readyForFirstPaint && !this.initialLoadDone()) {
        this.initialLoadDone.set(true);
      }
    });
  }

  public viewLabel(view: IKanbanViewOption): string {
    return view.isDefault ? this.translate.instant('page.kanbanConfig.defaultViewName') : view.name;
  }

  public override ngOnInit(): void {
    const sort: Sort = [{ active: 'rank', direction: 'asc' }] as unknown as Sort;

    this.datatableParams$.next({
      limit: 150,
      page: 1,
      sort,
    });

    // The mat-dropdown reports selection via its FormControl; translate that into a view change.
    this.subscriptions.push(
      this.viewControl.valueChanges.subscribe((value: IDropdownOption[] | null) => {
        const viewId: number | null = value?.[0] ? Number(value[0].id) : null;

        if (viewId !== null && viewId !== this.selectedViewId) {
          this.onViewChange(viewId);
        }
      }),
    );

    this.loadViews();

    super.ngOnInit();
  }

  public onViewChange(viewId: number): void {
    this.selectedViewId = viewId;
    this.columnsLoaded.set(false);
    localStorage.setItem(KANBAN_VIEW_STORAGE_KEY, String(viewId));
    this.loadColumns(viewId);
  }

  private loadViews(): void {
    this.subscriptions.push(
      this.kanbanColumnService.getViews().subscribe((response) => {
        this.kanbanViews.set(response.data);
        this.viewsLoaded.set(true);

        const storedId = Number(localStorage.getItem(KANBAN_VIEW_STORAGE_KEY));
        const initialView: IKanbanViewOption | undefined =
          response.data.find((view: IKanbanViewOption) => view.id === storedId) ?? response.data[0];

        if (initialView) {
          // Setting the control drives onViewChange through valueChanges above.
          this.viewControl.setValue([{ id: initialView.id, name: this.viewLabel(initialView) }]);
        }
      }),
    );
  }

  private loadColumns(viewId: number): void {
    this.subscriptions.push(
      this.kanbanColumnService.getColumns(viewId).subscribe((response) => {
        this.columnDefs.set(
          response.data.map(
            (column: IKanbanColumn): IKanbanColumnDef => ({
              id: column.id,
              title: column.title,
              workflowStepIds: column.workflowStepIds ?? [],
            }),
          ),
        );
        this.columnsLoaded.set(true);
      }),
    );
  }
}
